import React, { useEffect, useMemo, useState } from 'react';
import { useScheduleActionLock } from '../../hook/useScheduleActionLock';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body2Regular, CaptionLight } from '../../lib/font';
import Modal from '../modal/Modal';
import { getCoachNameListAPI } from '../../api/center';
import { useTemplates, useCreateSession } from '@/features/groupSession/hooks';
import { usePublicMemberList } from '@/features/publicMember/hooks';
import { useMemberTickets, useCenterMemberTickets, useTickets, useCreateMemberTicket } from '@/features/groupTicket/hooks';
import { Ticket } from '@/features/groupTicket/types';
import { GroupClassTemplate, SessionEntry, PreBookMember, CreateSessionBody } from '@/features/groupSession/types';
import dayjs from 'dayjs';
import { formatKoreanPhoneNumber } from '../../utils/phoneNumber';
import { MembershipPauseState } from '@/features/membershipPause/types';
import { isPassInUse } from '@/features/membershipPause/status';
import { toSmartScheduleKst } from '@/features/smartSchedule/date';
import useMembershipPauseCreateGate from '../MembershipPause/useMembershipPauseCreateGate';

type SessionMode = 'single' | 'recurring' | 'multi';

interface SelectedPreBookMember extends PreBookMember {
    memberName: string;
    ticketName: string;
    remaining: number;
    /** 수강권 일시정지 — 생성 확인 창 판정용(요청 본문에는 싣지 않는다). 새로 발급한 수강권은 없음 */
    pauseCheck?: PreBookPauseCheck;
}

type PreBookPauseCheck = {
    pause?: MembershipPauseState | null;
    status?: string;
    expiryDate?: string | null;
    isShared?: boolean;
    ownerName?: string;
};

const toPreBookPauseCheck = (mt: any): PreBookPauseCheck | undefined =>
    mt
        ? {
              pause: mt.pause ?? null,
              status: mt.status,
              expiryDate: mt.expiryDate ?? null,
              isShared: mt.isShared === true || mt.access?.type === 'SHARED',
              ownerName: mt.ownerName || mt.access?.ownerName || '',
          }
        : undefined;

/** 반복 생성될 날짜들 — getRecurringSessionCountInfo 와 같은 규칙(상한 + 1 까지만) */
const listRecurringDates = (startDate: string, endDate: string, weekdays: Set<number>) => {
    const dates: string[] = [];
    if (!startDate || !endDate || weekdays.size === 0) return dates;
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) return dates;
    let cursor = start;
    while ((cursor.isSame(end, 'day') || cursor.isBefore(end, 'day')) && dates.length <= MAX_GROUP_SESSION_CREATE_COUNT) {
        if (weekdays.has(cursor.day())) dates.push(cursor.format('YYYY-MM-DD'));
        cursor = cursor.add(1, 'day');
    }
    return dates;
};

const CLASS_TYPE_COLORS: Record<string, string> = {
    GROUP: '#10B981', PRIVATE_1ON2: '#3B82F6', PRIVATE_1ON3: '#8B5CF6',
};
const CLASS_TYPE_LABEL: Record<string, string> = {
    GROUP: '그룹', PRIVATE_1ON2: '1:2', PRIVATE_1ON3: '1:3',
};

const WEEKDAY_LABEL = ['일', '월', '화', '수', '목', '금', '토'];
const MAX_GROUP_SESSION_CREATE_COUNT = 200;
const GROUP_SESSION_LIMIT_MESSAGE = `그룹수업은 한 번에 최대 ${MAX_GROUP_SESSION_CREATE_COUNT}개까지 생성할 수 있습니다. 기간을 나눠서 등록해주세요.`;

const getRecurringSessionCountInfo = (startDate: string, endDate: string, weekdays: Set<number>) => {
    if (!startDate || !endDate || weekdays.size === 0) {
        return { count: 0, limitEndDate: null as string | null, isOverLimit: false };
    }

    const start = dayjs(startDate);
    const end = dayjs(endDate);
    if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) {
        return { count: 0, limitEndDate: null as string | null, isOverLimit: false };
    }

    let count = 0;
    let limitEndDate: string | null = null;
    let cursor = start;

    while (cursor.isSame(end, 'day') || cursor.isBefore(end, 'day')) {
        if (weekdays.has(cursor.day())) {
            count += 1;
            if (count === MAX_GROUP_SESSION_CREATE_COUNT) {
                limitEndDate = cursor.format('YYYY-MM-DD');
            }
            if (count > MAX_GROUP_SESSION_CREATE_COUNT) {
                return { count, limitEndDate, isOverLimit: true };
            }
        }
        cursor = cursor.add(1, 'day');
    }

    return { count, limitEndDate, isOverLimit: false };
};

const inputStyle: React.CSSProperties = {
    border: '1px solid #E1E4E6', borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none',
};

const calcDuration = (start: string, end: string) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    return (eh * 60 + em) - (sh * 60 + sm);
};

const addMinutes = (start: string, minutes: number) => {
    const [h, m] = start.split(':').map(Number);
    const total = h * 60 + m + minutes;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const QuickGroupSessionModal = ({
    onClose, onOpenFullCreate, createAlert,
    defaultDate, defaultStartTime, defaultEndTime, defaultCoachId, defaultCoachName,
}: {
    onClose: () => void; onOpenFullCreate: () => void; createAlert: any;
    defaultDate?: string; defaultStartTime?: string; defaultEndTime?: string;
    defaultCoachId?: string; defaultCoachName?: string;
}) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const [authorization] = useState(getItemWithExpiration('authorization'));
    const centerId = authObject?.centerId;

    const navigate = useNavigate();
    /** 스케줄 동작 잠금(생성) — 선택창에서 이미 물었지만 저장 직전 재검사(30분 만료)는 여기서 */
    const scheduleLock = useScheduleActionLock('schedule.create');
    const { data: templates = [], isLoading } = useTemplates({ centerId });
    const createMutation = useCreateSession();

    // 등록된 그룹수업 템플릿이 없으면 수강권 관리 > 그룹수업 수강권으로 바로 이동
    const shouldRedirect = !isLoading && templates.length === 0;
    useEffect(() => {
        if (shouldRedirect) {
            onClose();
            navigate('/classPass?tab=group&sub=tickets');
        }
    }, [shouldRedirect, navigate, onClose]);

    const [selectedTemplate, setSelectedTemplate] = useState<GroupClassTemplate | null>(null);

    // 모드
    const [mode, setMode] = useState<SessionMode>('single');

    // 단일
    const [date, setDate] = useState(defaultDate || dayjs().format('YYYY-MM-DD'));
    const [startTime, setStartTime] = useState(defaultStartTime || '10:00');
    const [endTime, setEndTime] = useState(defaultEndTime || '10:50');

    // 주간 반복
    const [recStart, setRecStart] = useState(defaultDate || dayjs().format('YYYY-MM-DD'));
    const [recEnd, setRecEnd] = useState(dayjs(defaultDate || undefined).add(4, 'week').format('YYYY-MM-DD'));
    const [recWeekdays, setRecWeekdays] = useState<Set<number>>(new Set());
    const [recStartTime, setRecStartTime] = useState(defaultStartTime || '10:00');
    const [recEndTime, setRecEndTime] = useState(defaultEndTime || '10:50');

    // 임의 N개
    const [multiSessions, setMultiSessions] = useState<SessionEntry[]>([]);
    const [multiNewDate, setMultiNewDate] = useState(defaultDate || dayjs().format('YYYY-MM-DD'));
    const [multiNewStart, setMultiNewStart] = useState(defaultStartTime || '10:00');
    const [multiNewEnd, setMultiNewEnd] = useState(defaultEndTime || '10:50');

    const [showDetails, setShowDetails] = useState(false);

    // 오버라이드
    const [overrideCoachId, setOverrideCoachId] = useState(defaultCoachId || '');
    const [overrideMaxCapacity, setOverrideMaxCapacity] = useState<number | null>(null);
    const [overrideMinCapacity, setOverrideMinCapacity] = useState<number | null>(null);
    const [overrideWaitlistLimit, setOverrideWaitlistLimit] = useState<number | null>(null);
    const [overrideRoomId, setOverrideRoomId] = useState<string | null>(null);

    // 사전 예약 회원
    const [preBook, setPreBook] = useState<SelectedPreBookMember[]>([]);
    const [showMemberSearch, setShowMemberSearch] = useState(false);
    const [memberSearchQuery, setMemberSearchQuery] = useState('');
    const [pickMember, setPickMember] = useState<{ memberId: string; memberName: string } | null>(null);
    const [showIssuePanel, setShowIssuePanel] = useState(false);
    const [issueTicketId, setIssueTicketId] = useState('');

    // 강사 목록
    const { data: memberData } = usePublicMemberList({ centerId, memberType: 'all', limit: 200 });
    const { data: coachNameListResponse } = useQuery(
        ['groupSessionCoachNameList', centerId],
        () => getCoachNameListAPI(centerId, authorization),
        { enabled: !!centerId && !!authorization, staleTime: 5 * 60 * 1000 },
    );
    const coachList = useMemo(() => {
        const map = new Map<string, string>();
        (coachNameListResponse?.data?.coachNameList ?? []).forEach((coach: any) => {
            if (coach.coachId && !map.has(coach.coachId)) {
                map.set(coach.coachId, coach.name || coach.coachDisplayAlias || coach.coachName || coach.coachId);
            }
        });
        return Array.from(map.entries()).map(([code, value]) => ({ code, value }));
    }, [coachNameListResponse]);

    // 회원 검색
    const { data: memberSearchData } = usePublicMemberList({
        centerId, search: memberSearchQuery || undefined, memberType: 'all', limit: 20,
        enabled: showMemberSearch && memberSearchQuery.length >= 1,
    });
    const searchResults = memberSearchData?.results ?? [];
    const preBookIds = new Set(preBook.map((p) => p.memberId));
    const availableSearchMembers = searchResults.filter((m: any) => !preBookIds.has(m.memberId || m._id));

    // 선택된 회원의 수강권 목록.
    // includeShared: 공유만 받은 회원은 소유 티켓이 없어 '사용 가능한 수강권 없음'으로 보이고
    // 신규 발급 플로우로 밀려난다. 직원 예약은 서버가 이미 허용하므로 공유 행까지 받는다.
    const { data: memberTickets = [] } = useMemberTickets({
        centerId, memberId: pickMember?.memberId || '', status: 'ACTIVE',
        enabled: !!pickMember, includeShared: true,
    });
    // 백엔드가 populated 객체로 내려줄 수 있으므로 _id로 정규화
    const allowedTicketIds = useMemo(() => {
        const raw = (selectedTemplate?.allowedTicketIds ?? []) as any[];
        return raw.map((t) => (typeof t === 'object' ? t?._id : t)).filter(Boolean) as string[];
    }, [selectedTemplate]);
    const matchingTickets = (memberTickets as any[]).filter((mt: any) => {
        const tid = typeof mt.ticketId === 'object' ? mt.ticketId?._id : mt.ticketId;
        return allowedTicketIds.includes(tid) && mt.remainingCount > 0;
    });

    // 수강권 보유 기반 사전예약 가능 회원 목록 (검색어 없을 때 보여줌)
    const { data: centerTicketData, isLoading: isCenterTicketsLoading } = useCenterMemberTickets({
        centerId, params: { status: 'ACTIVE', limit: '1000', activeMembersOnly: 'true', includeShared: 'true' },
        enabled: showMemberSearch && memberSearchQuery.length === 0 && !!selectedTemplate,
    });
    const memberNameMap = useMemo(() => {
        const m = new Map<string, string>();
        (memberData?.results ?? []).forEach((it: any) => {
            m.set(it.memberId || it._id, it.name);
        });
        return m;
    }, [memberData]);
    const totalScannedTickets = centerTicketData?.results?.length ?? 0;
    const eligibleMembers = useMemo(() => {
        const map = new Map<string, { memberId: string; memberName: string; tickets: { mtId: string; name: string; remaining: number; isShared?: boolean; ownerName?: string }[] }>();
        if (!centerTicketData?.results) return [];
        for (const mt of centerTicketData.results as any[]) {
            const tid = typeof mt.ticketId === 'object' ? mt.ticketId?._id : mt.ticketId;
            // 허용 수강권 지정이 있으면 필터, 없으면 모든 수강권 허용
            if (allowedTicketIds.length > 0 && !allowedTicketIds.includes(tid)) continue;
            if (!(mt.remainingCount > 0)) continue;
            if (preBookIds.has(mt.memberId)) continue;
            if (!map.has(mt.memberId)) {
                map.set(mt.memberId, {
                    memberId: mt.memberId,
                    memberName: memberNameMap.get(mt.memberId) || mt.memberName || '(이름 없음)',
                    tickets: [],
                });
            }
            map.get(mt.memberId)!.tickets.push({
                mtId: mt._id,
                name: mt.ticketName || (typeof mt.ticketId === 'object' ? mt.ticketId?.name : '') || '수강권',
                remaining: mt.remainingCount,
                isShared: mt.isShared === true,
                ownerName: mt.ownerName || '',
            });
        }
        return Array.from(map.values()).sort((a, b) => a.memberName.localeCompare(b.memberName));
    }, [centerTicketData, allowedTicketIds, preBookIds, memberNameMap]);

    const addPreBookFromEligible = (memberId: string, memberName: string, mtId: string, name: string, remaining: number) => {
        // 센터 목록은 소유자 행 뒤에 같은 수강권 ID 의 참여자 행을 붙인다 — 고른 회원의 행을 찾는다(검증 8).
        const rows = (centerTicketData?.results ?? []) as any[];
        const row = rows.find((mt) => mt?._id === mtId && mt?.memberId === memberId) ?? rows.find((mt) => mt?._id === mtId);
        setPreBook((prev) => [...prev, {
            memberId, memberTicketId: mtId, memberName,
            ticketName: name, remaining,
            pauseCheck: toPreBookPauseCheck(row),
        }]);
    };

    // 수강권 발급 (해당 회원이 매칭 수강권이 없을 때)
    const { data: ticketProducts = [] } = useTickets({
        centerId, enabled: showIssuePanel && !!selectedTemplate,
    });
    const applicableTicketProducts = (ticketProducts as Ticket[]).filter((t) => {
        if (!t.isActive) return false;
        return allowedTicketIds.length === 0 || allowedTicketIds.includes(t._id);
    });
    const createMemberTicketMutation = useCreateMemberTicket();

    const handleIssueAndAdd = async () => {
        if (!pickMember || !issueTicketId) return;
        const product = applicableTicketProducts.find((t) => t._id === issueTicketId);
        if (!product) return;
        try {
            const result = await createMemberTicketMutation.mutateAsync({
                centerId, memberId: pickMember.memberId,
                body: {
                    ticketId: issueTicketId,
                    startDate: dayjs().format('YYYY-MM-DD'),
                    price: 0,
                    paymentMethod: 'cash',
                    paymentDate: dayjs().format('YYYY-MM-DD'),
                },
            });
            const mt: any = result?.data?.memberTicket;
            if (!mt?._id) {
                createAlert('발급 실패', '수강권이 발급되지 않았습니다. 다시 시도해주세요.', true);
                return;
            }
            setPreBook((prev) => [...prev, {
                memberId: pickMember.memberId,
                memberTicketId: mt._id,
                memberName: pickMember.memberName,
                ticketName: product.name,
                remaining: mt.remainingCount ?? product.totalCount ?? 0,
            }]);
            setPickMember(null);
            setShowMemberSearch(false);
            setMemberSearchQuery('');
            setShowIssuePanel(false);
            setIssueTicketId('');
        } catch (err: any) {
            createAlert('발급 실패', err?.serverMessage || err?.message || '수강권 발급에 실패했습니다.', true);
        }
    };

    const effectiveCoachId = overrideCoachId || selectedTemplate?.defaultCoachId || defaultCoachId || '';
    const effectiveCoachName = coachList.find((c) => c.code === effectiveCoachId)?.value
        || (effectiveCoachId ? (selectedTemplate?.defaultCoachName || defaultCoachName || '') : '');
    const effectiveMaxCapacity = overrideMaxCapacity ?? selectedTemplate?.maxCapacity ?? 6;
    const effectiveRoomId = overrideRoomId ?? selectedTemplate?.roomId ?? '';

    const recurringSessionCountInfo = useMemo(
        () => getRecurringSessionCountInfo(recStart, recEnd, recWeekdays),
        [recStart, recEnd, recWeekdays],
    );

    useEffect(() => {
        if (mode !== 'recurring') return;
        if (!recurringSessionCountInfo.isOverLimit || !recurringSessionCountInfo.limitEndDate) return;
        setRecEnd((prev) => (
            prev === recurringSessionCountInfo.limitEndDate ? prev : recurringSessionCountInfo.limitEndDate!
        ));
    }, [mode, recurringSessionCountInfo.isOverLimit, recurringSessionCountInfo.limitEndDate]);

    // 예상 세션 수 계산
    const estimatedSessionCount = (() => {
        if (mode === 'single') return 1;
        if (mode === 'multi') return multiSessions.length;
        if (mode === 'recurring') {
            return recurringSessionCountInfo.count;
        }
        return 0;
    })();

    const ticketDeductionTotal = estimatedSessionCount * preBook.length;

    const durationMinutes = calcDuration(startTime, endTime);
    const setDuration = (mins: number) => {
        if (!startTime) return;
        setEndTime(addMinutes(startTime, mins));
    };
    const handleStartTimeChange = (newStart: string) => {
        const prev = durationMinutes || (selectedTemplate?.defaultDuration ?? 50);
        setStartTime(newStart);
        setEndTime(addMinutes(newStart, prev));
    };

    const DURATION_PRESETS = [30, 50, 60, 90];

    const handleAddMultiSession = () => {
        if (multiSessions.length >= MAX_GROUP_SESSION_CREATE_COUNT) {
            createAlert('입력 오류', GROUP_SESSION_LIMIT_MESSAGE, true);
            return;
        }
        if (!multiNewDate) return;
        if (multiNewStart >= multiNewEnd) {
            createAlert('입력 오류', '종료 시간이 시작 시간보다 늦어야 합니다.', true);
            return;
        }
        const duplicate = multiSessions.some((s) =>
            s.date === multiNewDate && s.startTime === multiNewStart && s.endTime === multiNewEnd);
        if (duplicate) {
            createAlert('중복 세션', '동일 시간대 세션이 이미 추가되어 있습니다.', true);
            return;
        }
        setMultiSessions((prev) => [...prev, { date: multiNewDate, startTime: multiNewStart, endTime: multiNewEnd }]
            .sort((a, b) => `${a.date} ${a.startTime}`.localeCompare(`${b.date} ${b.startTime}`)));
    };
    const removeMultiSession = (idx: number) => setMultiSessions((prev) => prev.filter((_, i) => i !== idx));

    const toggleWeekday = (d: number) => {
        setRecWeekdays((prev) => {
            const next = new Set(prev);
            if (next.has(d)) next.delete(d); else next.add(d);
            return next;
        });
    };

    const addPreBookMember = (memberTicket: any) => {
        if (!pickMember) return;
        const tid = typeof memberTicket.ticketId === 'object' ? memberTicket.ticketId?._id : memberTicket.ticketId;
        const ticketName = memberTicket.ticketName
            || (typeof memberTicket.ticketId === 'object' ? memberTicket.ticketId?.name : '')
            || '수강권';
        setPreBook((prev) => [...prev, {
            memberId: pickMember.memberId,
            memberTicketId: memberTicket._id,
            memberName: pickMember.memberName,
            ticketName,
            remaining: memberTicket.remainingCount ?? 0,
            pauseCheck: toPreBookPauseCheck(memberTicket),
        }]);
        setPickMember(null);
        setShowMemberSearch(false);
        setMemberSearchQuery('');
    };
    const removePreBookMember = (memberId: string) =>
        setPreBook((prev) => prev.filter((p) => p.memberId !== memberId));

    const buildRequestBody = (): CreateSessionBody | null => {
        if (!selectedTemplate) return null;
        const base: CreateSessionBody = {
            templateId: selectedTemplate._id,
            coachId: effectiveCoachId || undefined,
            maxCapacity: overrideMaxCapacity ?? undefined,
            minCapacity: overrideMinCapacity ?? undefined,
            waitlistLimit: overrideWaitlistLimit ?? undefined,
            roomId: overrideRoomId ?? undefined,
        };
        if (preBook.length > 0) {
            base.preBookMembers = preBook.map((p) => ({ memberId: p.memberId, memberTicketId: p.memberTicketId }));
        }
        if (mode === 'single') {
            base.isRecurring = false;
            base.date = date;
            base.startTime = startTime;
            base.endTime = endTime;
        } else if (mode === 'recurring') {
            base.isRecurring = true;
            base.recurrence = {
                startDate: recStart,
                endDate: recEnd,
                weekdays: Array.from(recWeekdays).sort().map((d) => ({
                    day: d, startTime: recStartTime, endTime: recEndTime,
                })),
            };
        } else {
            base.sessions = multiSessions;
        }
        return base;
    };

    const validate = (): string | null => {
        if (!selectedTemplate) return '수업을 선택해주세요.';
        if (!effectiveCoachId) return '담당 강사를 선택해주세요.';
        if (mode === 'single') {
            if (!date) return '날짜를 선택해주세요.';
            if (startTime >= endTime) return '종료 시간이 시작 시간보다 늦어야 합니다.';
        } else if (mode === 'recurring') {
            if (!recStart || !recEnd) return '시작/종료 날짜를 입력해주세요.';
            if (recStart > recEnd) return '종료 날짜가 시작 날짜보다 늦어야 합니다.';
            if (recWeekdays.size === 0) return '반복 요일을 하나 이상 선택해주세요.';
            if (recStartTime >= recEndTime) return '종료 시간이 시작 시간보다 늦어야 합니다.';
            if (estimatedSessionCount > MAX_GROUP_SESSION_CREATE_COUNT) return GROUP_SESSION_LIMIT_MESSAGE;
        } else {
            if (multiSessions.length === 0) return '최소 1개 이상의 세션을 추가해주세요.';
            if (multiSessions.length > MAX_GROUP_SESSION_CREATE_COUNT) return GROUP_SESSION_LIMIT_MESSAGE;
        }
        return null;
    };

    const pauseGate = useMembershipPauseCreateGate({ createAlert });

    /** 이번 생성으로 만들어질 수업들의 시작 시각(KST 벽시계 → 시각) */
    const plannedStartTimes = (): string[] => {
        const toInstant = (d: string, hm: string) => toSmartScheduleKst(`${d}T${hm}:00`).toISOString();
        if (mode === 'single') return date && startTime ? [toInstant(date, startTime)] : [];
        if (mode === 'recurring') return listRecurringDates(recStart, recEnd, recWeekdays).map((d) => toInstant(d, recStartTime));
        return multiSessions.map((s) => toInstant(s.date, s.startTime));
    };

    const handleCreate = async () => {
        const err = validate();
        if (err) { createAlert('입력 오류', err, true); return; }
        const body = buildRequestBody();
        if (!body) return;
        // 스케줄 동작 잠금 재검사 — 정지 해제(첫 부수 쓰기)보다 **먼저** 본다(설계 §6-6)
        if (scheduleLock.requiresCheck() && !(await scheduleLock.ensure())) return;

        // 사전 예약 회원의 수강권이 정지 기간이면 확인 창(G1). 새로 발급한 수강권은 정지가 없다.
        if (preBook.length > 0) {
            const startTimes = plannedStartTimes();
            const proceed = await pauseGate.confirmBeforeCreate(
                preBook
                    .filter((p) => !!p.pauseCheck)
                    .map((p) => ({
                        ref: { kind: 'group' as const, centerId, memberTicketId: p.memberTicketId },
                        pause: p.pauseCheck?.pause,
                        inUse: isPassInUse(p.pauseCheck),
                        memberName: p.memberName,
                        passName: p.ticketName,
                        startTimes,
                        expiry: p.pauseCheck?.expiryDate ?? null,
                        isShared: p.pauseCheck?.isShared === true,
                        sharedOwnerName: p.pauseCheck?.isShared ? p.pauseCheck?.ownerName || null : null,
                    })),
            );
            if (!proceed) return;
        }

        try {
            const res = await createMutation.mutateAsync({ centerId, body });
            const data = res?.data;
            const created = data?.sessionsCreated ?? 1;
            const failed = data?.bookingsFailed ?? [];
            const confirmed = (data?.bookingsCreated ?? []).filter((b) => b.status === 'CONFIRMED').length;
            const waitlisted = (data?.bookingsCreated ?? []).filter((b) => b.status === 'WAITLISTED').length;

            let msg = `"${selectedTemplate!.title}" ${created}개 세션이 생성되었습니다.`;
            if (preBook.length > 0) {
                msg += `\n예약 확정 ${confirmed}건`;
                if (waitlisted > 0) msg += ` · 대기 ${waitlisted}건`;
                if (failed.length > 0) msg += ` · 실패 ${failed.length}건`;
            }
            if (failed.length > 0) {
                const memberNameMap = new Map<string, string>(preBook.map((p) => [p.memberId, p.memberName]));
                const sessionDateMap = new Map<string, string>();
                (data?.sessions ?? []).forEach((s: any) => {
                    sessionDateMap.set(s._id, `${dayjs(s.startTime).format('MM/DD HH:mm')}`);
                });
                const lines = failed.slice(0, 5).map((f) => {
                    const name = memberNameMap.get(f.memberId) || f.memberId;
                    const when = sessionDateMap.get(f.sessionId) || '';
                    return `• ${name}${when ? ` · ${when}` : ''} — ${f.reason}`;
                });
                const extra = failed.length > 5 ? `\n외 ${failed.length - 5}건` : '';
                // 「풀고 등록」 뒤 개별 예약이 실패해도 정지가 풀린 사실을 알린다(검증 4). 세션·예약 수는 그대로.
                createAlert('일부 회원 예약 실패', pauseGate.decorateFailureMessage(`${msg}\n\n${lines.join('\n')}${extra}`), true);
            } else {
                createAlert('생성 완료', msg, false);
            }
            onClose();
        } catch (e: any) {
            createAlert('생성 실패', pauseGate.decorateFailureMessage(e?.serverMessage || e?.message || '수업 생성에 실패했습니다.'), true);
        }
    };

    const modeTabStyle = (active: boolean): React.CSSProperties => ({
        flex: 1, padding: '10px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
        cursor: 'pointer', textAlign: 'center',
        border: active ? '2px solid #3B82F6' : '1.5px solid #E5E7EB',
        backgroundColor: active ? '#EFF6FF' : 'white',
        color: active ? '#1D4ED8' : '#6B7280',
        transition: 'all 0.15s',
    });

    // 리다이렉트 중이거나 템플릿 로딩 중이면 빈 UI 노출 방지
    if (isLoading || shouldRedirect) return null;

    return (
        <Modal isCloseButton setIsVisible={onClose} whiteViewWidth={640}>
            <Col style={{ padding: '36px 36px 28px', overflow: 'auto', maxHeight: '85vh' }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
                    수업 생성
                </span>
                <CaptionLight color={colors.LAVEL_1} style={{ marginBottom: 24 }}>
                    수업을 선택하고 단일/반복/임의 일정 모드를 골라 만드세요.
                </CaptionLight>

                {/* 수업 선택 */}
                <Col style={{ marginBottom: 20 }}>
                    <Body2Regular color={colors.LAVEL_2} style={{ marginBottom: 10, fontWeight: 600 }}>수업 선택</Body2Regular>
                    {isLoading ? (
                        <span style={{ fontSize: 14, color: '#9CA3AF' }}>불러오는 중...</span>
                    ) : templates.length === 0 ? (
                        <Col style={{ padding: '24px 0', alignItems: 'center', gap: 8, backgroundColor: '#F9FAFB', borderRadius: 10, border: '1px dashed #E5E7EB' }}>
                            <span style={{ fontSize: 14, color: '#9CA3AF' }}>수업 관리 페이지로 이동합니다...</span>
                        </Col>
                    ) : (
                        <Col style={{ gap: 8, maxHeight: 240, overflow: 'auto', paddingRight: 4 }}>
                            {(templates as GroupClassTemplate[]).map((t) => {
                                const isSelected = selectedTemplate?._id === t._id;
                                const color = CLASS_TYPE_COLORS[t.classType] || '#10B981';
                                return (
                                    <Row key={t._id}
                                        onClick={() => {
                                            // 같은 템플릿 재클릭이면 변경 없이 종료 (사전예약 등 파괴 방지)
                                            if (selectedTemplate?._id === t._id) return;
                                            setSelectedTemplate(t);
                                            if (!overrideCoachId) setOverrideCoachId(defaultCoachId || t.defaultCoachId || '');
                                            setOverrideMaxCapacity(null);
                                            setOverrideMinCapacity(null);
                                            setOverrideWaitlistLimit(null);
                                            setOverrideRoomId(null);
                                            setShowDetails(false);
                                            // 허용 수강권이 달라지면 사전예약 유효성이 깨지므로 초기화
                                            setPreBook([]);
                                            if (startTime) setEndTime(addMinutes(startTime, t.defaultDuration ?? 50));
                                            if (recStartTime) setRecEndTime(addMinutes(recStartTime, t.defaultDuration ?? 50));
                                            if (multiNewStart) setMultiNewEnd(addMinutes(multiNewStart, t.defaultDuration ?? 50));
                                        }}
                                        style={{
                                            padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
                                            border: isSelected ? `2px solid ${color}` : '1.5px solid #E5E7EB',
                                            backgroundColor: isSelected ? `${color}0D` : 'white',
                                            alignItems: 'center', justifyContent: 'space-between',
                                            transition: 'all 0.15s',
                                        }}>
                                        <Row style={{ alignItems: 'center', gap: 12 }}>
                                            <div style={{ width: 5, height: 36, borderRadius: 3, backgroundColor: color, flexShrink: 0 }} />
                                            <Col>
                                                <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{t.title}</span>
                                                <span style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                                                    {CLASS_TYPE_LABEL[t.classType]} · {t.category} · 정원 {t.maxCapacity}명 · {t.defaultDuration ?? 50}분
                                                </span>
                                            </Col>
                                        </Row>
                                        {isSelected && (
                                            <span style={{
                                                width: 24, height: 24, borderRadius: '50%', display: 'flex',
                                                alignItems: 'center', justifyContent: 'center',
                                                backgroundColor: color, color: 'white', fontSize: 13, fontWeight: 700,
                                            }}>
                                                ✓
                                            </span>
                                        )}
                                    </Row>
                                );
                            })}
                        </Col>
                    )}
                </Col>

                {/* 선택된 수업 요약 + 세션 오버라이드 토글 */}
                {selectedTemplate && (
                    <Col style={{ marginBottom: 20, padding: 16, backgroundColor: '#F9FAFB', borderRadius: 12, border: '1px solid #E5E7EB' }}>
                        <Row style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{
                                fontSize: 14, fontWeight: 600, padding: '8px 14px', borderRadius: 8,
                                backgroundColor: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0',
                            }}>
                                강사 · {effectiveCoachName || '선택 필요'}
                            </span>
                            <span style={{
                                fontSize: 14, fontWeight: 600, padding: '8px 14px', borderRadius: 8,
                                backgroundColor: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE',
                            }}>
                                정원 · {effectiveMaxCapacity}명
                            </span>
                            {effectiveRoomId && (
                                <span style={{
                                    fontSize: 14, fontWeight: 600, padding: '8px 14px', borderRadius: 8,
                                    backgroundColor: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A',
                                }}>
                                    장소 · {effectiveRoomId}
                                </span>
                            )}
                        </Row>

                        <button onClick={() => setShowDetails(!showDetails)}
                            style={{
                                marginTop: 12, padding: '8px 12px', background: 'white',
                                border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer',
                                fontSize: 13, fontWeight: 500, color: '#374151',
                                display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
                            }}>
                            <span style={{ fontSize: 12 }}>{showDetails ? '▼' : '▶'}</span>
                            ⚙️ 이 세션만 조정
                        </button>

                        {showDetails && (
                            <Col style={{ marginTop: 14, gap: 12 }}>
                                {selectedTemplate.allowedTicketNames && selectedTemplate.allowedTicketNames.length > 0 && (
                                    <Col>
                                        <span style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4 }}>사용 가능 수강권</span>
                                        <Row style={{ gap: 4, flexWrap: 'wrap' }}>
                                            {selectedTemplate.allowedTicketNames.map((name, i) => (
                                                <span key={i} style={{ padding: '3px 10px', borderRadius: 6, fontSize: 12, backgroundColor: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0' }}>{name}</span>
                                            ))}
                                        </Row>
                                    </Col>
                                )}
                                <Col style={{ gap: 4 }}>
                                    <span style={{ fontSize: 12, color: '#6B7280' }}>담당 강사</span>
                                    {/* 고르지 않으면 수업 기본 강사·캘린더 강사가 들어간다 — 선택 칸에도 실제로 들어갈 강사를 보여 준다(강사 없는 수업은 만들지 않는다) */}
                                    <select value={effectiveCoachId} onChange={(e) => setOverrideCoachId(e.target.value)}
                                        style={{ ...inputStyle, fontSize: 13 }}>
                                        {!effectiveCoachId && <option value="" disabled>강사를 선택해 주세요</option>}
                                        {effectiveCoachId && !coachList.some((c) => c.code === effectiveCoachId) && (
                                            <option value={effectiveCoachId}>{effectiveCoachName || '현재 강사'}</option>
                                        )}
                                        {coachList.map((c) => <option key={c.code} value={c.code}>{c.value}</option>)}
                                    </select>
                                </Col>
                                <Row style={{ gap: 10 }}>
                                    <Col style={{ flex: 1, gap: 4 }}>
                                        <span style={{ fontSize: 12, color: '#6B7280' }}>최대 정원</span>
                                        <input type="number" value={overrideMaxCapacity ?? selectedTemplate.maxCapacity}
                                            onChange={(e) => setOverrideMaxCapacity(Number(e.target.value))}
                                            min={1} style={{ ...inputStyle, fontSize: 13 }} />
                                    </Col>
                                    <Col style={{ flex: 1, gap: 4 }}>
                                        <span style={{ fontSize: 12, color: '#6B7280' }}>최소 인원</span>
                                        <input type="number" value={overrideMinCapacity ?? selectedTemplate.minCapacity}
                                            onChange={(e) => setOverrideMinCapacity(Number(e.target.value))}
                                            min={0} style={{ ...inputStyle, fontSize: 13 }} />
                                    </Col>
                                    <Col style={{ flex: 1, gap: 4 }}>
                                        <span style={{ fontSize: 12, color: '#6B7280' }}>대기</span>
                                        <input type="number" value={overrideWaitlistLimit ?? selectedTemplate.waitlistLimit}
                                            onChange={(e) => setOverrideWaitlistLimit(Number(e.target.value))}
                                            min={0} style={{ ...inputStyle, fontSize: 13 }} />
                                    </Col>
                                </Row>
                                <Col style={{ gap: 4 }}>
                                    <span style={{ fontSize: 12, color: '#6B7280' }}>장소</span>
                                    <input value={overrideRoomId ?? selectedTemplate.roomId ?? ''}
                                        onChange={(e) => setOverrideRoomId(e.target.value)}
                                        placeholder="장소" style={{ ...inputStyle, fontSize: 13 }} />
                                </Col>
                            </Col>
                        )}
                    </Col>
                )}

                {/* 모드 토글 */}
                {templates.length > 0 && (
                    <Col style={{ marginBottom: 20, gap: 10 }}>
                        <Body2Regular color={colors.LAVEL_2} style={{ fontWeight: 600 }}>일정 모드</Body2Regular>
                        <Row style={{ gap: 8 }}>
                            <div style={modeTabStyle(mode === 'single')} onClick={() => setMode('single')}>단일</div>
                            <div style={modeTabStyle(mode === 'recurring')} onClick={() => setMode('recurring')}>주간 반복</div>
                            <div style={modeTabStyle(mode === 'multi')} onClick={() => setMode('multi')}>임의 N개</div>
                        </Row>
                    </Col>
                )}

                {/* 단일 */}
                {templates.length > 0 && mode === 'single' && (
                    <Col style={{ gap: 12, marginBottom: 20 }}>
                        <Row style={{ gap: 10 }}>
                            <Col style={{ flex: 2, gap: 4 }}>
                                <CaptionLight color={colors.LAVEL_1}>날짜</CaptionLight>
                                <input style={inputStyle} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                            </Col>
                            <Col style={{ flex: 1, gap: 4 }}>
                                <CaptionLight color={colors.LAVEL_1}>시작 시간</CaptionLight>
                                <input style={inputStyle} type="time" value={startTime} onChange={(e) => handleStartTimeChange(e.target.value)} />
                            </Col>
                        </Row>
                        <Col style={{ gap: 8 }}>
                            <CaptionLight color={colors.LAVEL_1}>소요 시간</CaptionLight>
                            <Row style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                {DURATION_PRESETS.map((mins) => {
                                    const isSelected = durationMinutes === mins;
                                    return (
                                        <button key={mins} type="button" onClick={() => setDuration(mins)}
                                            style={{
                                                height: 40, padding: '0 18px', borderRadius: 10, fontSize: 14,
                                                fontWeight: isSelected ? 700 : 600,
                                                border: isSelected ? '2px solid #3B82F6' : '1.5px solid #E5E7EB',
                                                backgroundColor: isSelected ? '#EFF6FF' : 'white',
                                                color: isSelected ? '#1D4ED8' : '#374151',
                                                cursor: 'pointer',
                                            }}>
                                            {mins}분
                                        </button>
                                    );
                                })}
                            </Row>
                            <Row style={{ gap: 10, alignItems: 'center' }}>
                                <span style={{ fontSize: 13, color: '#6B7280' }}>직접 입력:</span>
                                <input style={{ ...inputStyle, width: 140, height: 40, fontSize: 14, padding: '0 12px' }}
                                    type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                                <span style={{ fontSize: 13, color: '#6B7280' }}>까지</span>
                            </Row>
                            <span style={{
                                fontSize: 13, color: '#1E40AF', fontWeight: 600,
                                backgroundColor: '#EFF6FF', padding: '6px 12px', borderRadius: 6,
                                alignSelf: 'flex-start',
                            }}>
                                📅 {startTime} ~ {endTime} · 총 {durationMinutes}분
                            </span>
                        </Col>
                    </Col>
                )}

                {/* 주간 반복 */}
                {templates.length > 0 && mode === 'recurring' && (
                    <Col style={{ gap: 12, marginBottom: 20 }}>
                        <Row style={{ gap: 10 }}>
                            <Col style={{ flex: 1, gap: 4 }}>
                                <CaptionLight color={colors.LAVEL_1}>시작 날짜</CaptionLight>
                                <input style={inputStyle} type="date" value={recStart}
                                    onChange={(e) => {
                                        const next = e.target.value;
                                        setRecStart(next);
                                        // 종료일이 시작일보다 앞이면 4주 뒤로 자동 조정
                                        if (next && recEnd && next > recEnd) {
                                            setRecEnd(dayjs(next).add(4, 'week').format('YYYY-MM-DD'));
                                        }
                                    }} />
                            </Col>
                            <Col style={{ flex: 1, gap: 4 }}>
                                <CaptionLight color={colors.LAVEL_1}>종료 날짜</CaptionLight>
                                <input style={inputStyle} type="date" value={recEnd} min={recStart || undefined}
                                    onChange={(e) => setRecEnd(e.target.value)} />
                            </Col>
                        </Row>
                        <Col style={{ gap: 6 }}>
                            <CaptionLight color={colors.LAVEL_1}>반복 요일</CaptionLight>
                            <Row style={{ gap: 6 }}>
                                {WEEKDAY_LABEL.map((label, idx) => {
                                    const selected = recWeekdays.has(idx);
                                    return (
                                        <button key={idx} type="button" onClick={() => toggleWeekday(idx)}
                                            style={{
                                                width: 44, height: 40, borderRadius: 10,
                                                fontSize: 14, fontWeight: selected ? 700 : 600,
                                                border: selected ? '2px solid #3B82F6' : '1.5px solid #E5E7EB',
                                                backgroundColor: selected ? '#EFF6FF' : 'white',
                                                color: selected ? '#1D4ED8' : '#374151',
                                                cursor: 'pointer',
                                            }}>
                                            {label}
                                        </button>
                                    );
                                })}
                            </Row>
                        </Col>
                        <Row style={{ gap: 10 }}>
                            <Col style={{ flex: 1, gap: 4 }}>
                                <CaptionLight color={colors.LAVEL_1}>시작 시간</CaptionLight>
                                <input style={inputStyle} type="time" value={recStartTime}
                                    onChange={(e) => {
                                        const dur = calcDuration(recStartTime, recEndTime) || (selectedTemplate?.defaultDuration ?? 50);
                                        setRecStartTime(e.target.value);
                                        setRecEndTime(addMinutes(e.target.value, dur));
                                    }} />
                            </Col>
                            <Col style={{ flex: 1, gap: 4 }}>
                                <CaptionLight color={colors.LAVEL_1}>종료 시간</CaptionLight>
                                <input style={inputStyle} type="time" value={recEndTime} onChange={(e) => setRecEndTime(e.target.value)} />
                            </Col>
                        </Row>
                        <span style={{
                            fontSize: 13,
                            color: estimatedSessionCount > MAX_GROUP_SESSION_CREATE_COUNT ? '#B91C1C' : '#1E40AF',
                            fontWeight: 600,
                            backgroundColor: estimatedSessionCount > MAX_GROUP_SESSION_CREATE_COUNT ? '#FEF2F2' : '#EFF6FF',
                            padding: '6px 12px',
                            borderRadius: 6,
                            alignSelf: 'flex-start',
                        }}>
                            📅 예상 {estimatedSessionCount > MAX_GROUP_SESSION_CREATE_COUNT
                                ? `${MAX_GROUP_SESSION_CREATE_COUNT}개 초과`
                                : `${estimatedSessionCount}개 세션 생성`} · 최대 {MAX_GROUP_SESSION_CREATE_COUNT}개
                        </span>
                    </Col>
                )}

                {/* 임의 N개 */}
                {templates.length > 0 && mode === 'multi' && (
                    <Col style={{ gap: 12, marginBottom: 20 }}>
                        <Row style={{ gap: 8, alignItems: 'flex-end' }}>
                            <Col style={{ flex: 2, gap: 4 }}>
                                <CaptionLight color={colors.LAVEL_1}>날짜</CaptionLight>
                                <input style={inputStyle} type="date" value={multiNewDate} onChange={(e) => setMultiNewDate(e.target.value)} />
                            </Col>
                            <Col style={{ flex: 1, gap: 4 }}>
                                <CaptionLight color={colors.LAVEL_1}>시작</CaptionLight>
                                <input style={inputStyle} type="time" value={multiNewStart}
                                    onChange={(e) => {
                                        const dur = calcDuration(multiNewStart, multiNewEnd) || (selectedTemplate?.defaultDuration ?? 50);
                                        setMultiNewStart(e.target.value);
                                        setMultiNewEnd(addMinutes(e.target.value, dur));
                                    }} />
                            </Col>
                            <Col style={{ flex: 1, gap: 4 }}>
                                <CaptionLight color={colors.LAVEL_1}>종료</CaptionLight>
                                <input style={inputStyle} type="time" value={multiNewEnd} onChange={(e) => setMultiNewEnd(e.target.value)} />
                            </Col>
                            <button type="button" onClick={handleAddMultiSession}
                                disabled={multiSessions.length >= MAX_GROUP_SESSION_CREATE_COUNT}
                                style={{
                                    height: 44, padding: '0 18px', borderRadius: 10, fontSize: 14, fontWeight: 600,
                                    backgroundColor: multiSessions.length >= MAX_GROUP_SESSION_CREATE_COUNT ? '#D1D5DB' : '#3B82F6',
                                    color: 'white',
                                    border: 'none',
                                    cursor: multiSessions.length >= MAX_GROUP_SESSION_CREATE_COUNT ? 'not-allowed' : 'pointer',
                                }}>
                                + 추가
                            </button>
                        </Row>
                        <span style={{
                            fontSize: 13,
                            color: multiSessions.length >= MAX_GROUP_SESSION_CREATE_COUNT ? '#B91C1C' : '#6B7280',
                            alignSelf: 'flex-start',
                        }}>
                            {multiSessions.length}/{MAX_GROUP_SESSION_CREATE_COUNT}개 추가됨
                        </span>
                        {multiSessions.length > 0 && (
                            <Col style={{ gap: 6, maxHeight: 180, overflow: 'auto' }}>
                                {multiSessions.map((s, i) => (
                                    <Row key={i} style={{
                                        alignItems: 'center', justifyContent: 'space-between',
                                        padding: '8px 12px', backgroundColor: '#F3F4F6', borderRadius: 8,
                                    }}>
                                        <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>
                                            {dayjs(s.date).format('MM/DD (dd)')} · {s.startTime} ~ {s.endTime}
                                        </span>
                                        <RemoveButton onClick={() => removeMultiSession(i)} ariaLabel="세션 삭제" />
                                    </Row>
                                ))}
                            </Col>
                        )}
                        <span style={{
                            fontSize: 13, color: '#1E40AF', fontWeight: 600,
                            backgroundColor: '#EFF6FF', padding: '6px 12px', borderRadius: 6,
                            alignSelf: 'flex-start',
                        }}>
                            📅 총 {multiSessions.length}개 세션
                        </span>
                    </Col>
                )}

                {/* 회원 사전 예약 */}
                {selectedTemplate && (
                    <Col style={{ marginBottom: 20, padding: 16, backgroundColor: '#FAFBFC', borderRadius: 12, border: '1px solid #E5E7EB', gap: 10 }}>
                        <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                            <Body2Regular color={colors.LAVEL_2} style={{ fontWeight: 600 }}>회원 사전 예약 (선택)</Body2Regular>
                            <button type="button" onClick={() => {
                                if (showMemberSearch) {
                                    setShowMemberSearch(false);
                                    setMemberSearchQuery('');
                                    setPickMember(null);
                                    setShowIssuePanel(false);
                                    setIssueTicketId('');
                                } else {
                                    setShowMemberSearch(true);
                                }
                            }}
                                style={{
                                    padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                                    backgroundColor: showMemberSearch ? '#E5E7EB' : '#3B82F6',
                                    color: showMemberSearch ? '#374151' : 'white',
                                    border: 'none', cursor: 'pointer',
                                }}>
                                {showMemberSearch ? '닫기' : '+ 회원 추가'}
                            </button>
                        </Row>

                        {preBook.length > 0 && (
                            <Col style={{ gap: 6 }}>
                                {preBook.map((p) => (
                                    <Row key={p.memberId} style={{
                                        alignItems: 'center', justifyContent: 'space-between',
                                        padding: '8px 12px', backgroundColor: 'white',
                                        borderRadius: 8, border: '1px solid #E5E7EB',
                                    }}>
                                        <Col style={{ gap: 2 }}>
                                            <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{p.memberName}</span>
                                            <span style={{ fontSize: 12, color: '#6B7280' }}>
                                                {p.ticketName} (잔여 {p.remaining})
                                            </span>
                                        </Col>
                                        <RemoveButton onClick={() => removePreBookMember(p.memberId)} ariaLabel={`${p.memberName} 제외`} />
                                    </Row>
                                ))}
                            </Col>
                        )}

                        {showMemberSearch && (
                            <Col style={{ gap: 8, padding: 12, backgroundColor: 'white', borderRadius: 10, border: '1px solid #E5E7EB' }}>
                                {!pickMember ? (
                                    <>
                                        <input value={memberSearchQuery}
                                            onChange={(e) => setMemberSearchQuery(e.target.value)}
                                            placeholder="회원 이름 또는 전화번호로 검색"
                                            style={{ ...inputStyle, fontSize: 13 }} />
                                        {memberSearchQuery.length >= 1 ? (
                                            <Col style={{ gap: 4, maxHeight: 220, overflow: 'auto' }}>
                                                {availableSearchMembers.length === 0 ? (
                                                    <span style={{ fontSize: 13, color: '#9CA3AF', padding: 8 }}>
                                                        검색 결과가 없습니다
                                                    </span>
                                                ) : (
                                                    availableSearchMembers.map((m: any) => (
                                                        <Row key={m.memberId || m._id}
                                                            onClick={() => setPickMember({ memberId: m.memberId || m._id, memberName: m.name })}
                                                            style={{
                                                                alignItems: 'center', justifyContent: 'space-between',
                                                                padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                                                                backgroundColor: '#F9FAFB',
                                                            }}>
                                                            <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{m.name}</span>
                                                            <span style={{ fontSize: 12, color: '#6B7280' }}>{formatKoreanPhoneNumber(m.phoneNumber, '')}</span>
                                                        </Row>
                                                    ))
                                                )}
                                            </Col>
                                        ) : (
                                            <Col style={{ gap: 6 }}>
                                                <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <span style={{ fontSize: 12, color: '#6B7280' }}>
                                                        예약 가능 회원 {eligibleMembers.length}명
                                                    </span>
                                                    <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                                                        {isCenterTicketsLoading ? '수강권 조회 중…'
                                                            : `수강권 ${totalScannedTickets}개 · 허용 ${allowedTicketIds.length || '전체'}`}
                                                    </span>
                                                </Row>

                                                {isCenterTicketsLoading ? (
                                                    <span style={{ fontSize: 13, color: '#9CA3AF', padding: 8 }}>불러오는 중…</span>
                                                ) : totalScannedTickets === 0 ? (
                                                    <span style={{ fontSize: 13, color: '#9CA3AF', padding: 8 }}>
                                                        센터에 활성 수강권이 없습니다. 회원 수강권을 먼저 발급해주세요.
                                                    </span>
                                                ) : eligibleMembers.length === 0 ? (
                                                    <span style={{ fontSize: 13, color: '#9CA3AF', padding: 8 }}>
                                                        {allowedTicketIds.length === 0
                                                            ? '잔여 횟수가 남은 수강권 보유 회원이 없습니다.'
                                                            : '이 수업의 허용 수강권을 가진 회원이 없습니다.'}
                                                    </span>
                                                ) : (
                                                    <Col style={{ gap: 6 }}>
                                                        {eligibleMembers.map((em) => (
                                                            <EligibleRow key={em.memberId} member={em}
                                                                onAdd={(mtId, name, remaining) =>
                                                                    addPreBookFromEligible(em.memberId, em.memberName, mtId, name, remaining)} />
                                                        ))}
                                                    </Col>
                                                )}
                                            </Col>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                            <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                                                {pickMember.memberName} · {showIssuePanel ? '수강권 발급' : '수강권 선택'}
                                            </span>
                                            <button onClick={() => {
                                                if (showIssuePanel) { setShowIssuePanel(false); setIssueTicketId(''); }
                                                else { setPickMember(null); setShowIssuePanel(false); setIssueTicketId(''); }
                                            }}
                                                style={{
                                                    padding: '4px 10px', fontSize: 12, borderRadius: 6,
                                                    border: '1px solid #D1D5DB', background: 'white', cursor: 'pointer',
                                                }}>
                                                {showIssuePanel ? '뒤로' : '다시 검색'}
                                            </button>
                                        </Row>

                                        {showIssuePanel ? (
                                            /* 수강권 발급 패널 */
                                            applicableTicketProducts.length === 0 ? (
                                                <span style={{ fontSize: 13, color: '#DC2626', padding: 8 }}>
                                                    이 수업에서 발급 가능한 수강권 상품이 없습니다
                                                </span>
                                            ) : (
                                                <Col style={{ gap: 6 }}>
                                                    <span style={{ fontSize: 12, color: '#6B7280' }}>
                                                        발급할 수강권 선택 (결제정보는 발급 후 회원 상세에서 수정 가능)
                                                    </span>
                                                    <Col style={{ gap: 4, maxHeight: 220, overflowY: 'auto' }}>
                                                        {applicableTicketProducts.map((t) => {
                                                            const selected = issueTicketId === t._id;
                                                            return (
                                                                <Row key={t._id}
                                                                    onClick={() => setIssueTicketId(t._id)}
                                                                    style={{
                                                                        alignItems: 'center', justifyContent: 'space-between',
                                                                        padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                                                                        border: selected ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                                                                        backgroundColor: selected ? '#EFF6FF' : 'white',
                                                                        flexShrink: 0,
                                                                    }}>
                                                                    <Col style={{ gap: 2 }}>
                                                                        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                                                                            {t.name}
                                                                        </span>
                                                                        <span style={{ fontSize: 11, color: '#6B7280' }}>
                                                                            {t.totalCount ? `${t.totalCount}회` : ''}
                                                                            {t.periodDays ? ` · ${t.periodDays}일` : ''}
                                                                            {typeof t.price === 'number' ? ` · ${t.price.toLocaleString()}원` : ''}
                                                                        </span>
                                                                    </Col>
                                                                    {selected && (
                                                                        <span style={{
                                                                            width: 20, height: 20, borderRadius: '50%',
                                                                            backgroundColor: '#3B82F6', color: 'white',
                                                                            fontSize: 11, fontWeight: 700,
                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                            flexShrink: 0,
                                                                        }}>✓</span>
                                                                    )}
                                                                </Row>
                                                            );
                                                        })}
                                                    </Col>
                                                    <button onClick={handleIssueAndAdd}
                                                        disabled={!issueTicketId || createMemberTicketMutation.isLoading}
                                                        style={{
                                                            marginTop: 4, height: 36, borderRadius: 8,
                                                            fontSize: 13, fontWeight: 700,
                                                            backgroundColor: '#10B981', color: 'white', border: 'none',
                                                            cursor: !issueTicketId || createMemberTicketMutation.isLoading ? 'not-allowed' : 'pointer',
                                                            opacity: !issueTicketId || createMemberTicketMutation.isLoading ? 0.5 : 1,
                                                        }}>
                                                        {createMemberTicketMutation.isLoading ? '발급 중...' : '발급하고 예약에 추가'}
                                                    </button>
                                                </Col>
                                            )
                                        ) : matchingTickets.length === 0 ? (
                                            <Col style={{ gap: 8 }}>
                                                <span style={{ fontSize: 13, color: '#DC2626', padding: 8 }}>
                                                    이 수업에서 사용할 수 있는 수강권이 없습니다
                                                </span>
                                                <button onClick={() => setShowIssuePanel(true)}
                                                    style={{
                                                        height: 36, borderRadius: 8, fontSize: 13, fontWeight: 600,
                                                        backgroundColor: '#10B981', color: 'white', border: 'none',
                                                        cursor: 'pointer',
                                                    }}>
                                                    + 새 수강권 발급하기
                                                </button>
                                            </Col>
                                        ) : (
                                            <Col style={{ gap: 4 }}>
                                                {matchingTickets.map((mt: any) => {
                                                    const ticketName = mt.ticketName
                                                        || (typeof mt.ticketId === 'object' ? mt.ticketId?.name : '')
                                                        || '수강권';
                                                    return (
                                                        <Row key={mt._id}
                                                            onClick={() => addPreBookMember(mt)}
                                                            style={{
                                                                alignItems: 'center', justifyContent: 'space-between',
                                                                padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                                                                backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0',
                                                            }}>
                                                            <span style={{ fontSize: 13, fontWeight: 500, color: '#065F46' }}>
                                                                {ticketName}
                                                                {/* 공유받은 회차라 소유자를 밝혀야 누구 회차를 쓰는지 알 수 있다. */}
                                                                {mt.access?.type === 'SHARED' && (
                                                                    <span style={{ fontSize: 11, color: '#0F766E', marginLeft: 6 }}>
                                                                        · 공유({mt.access.ownerName || '소유자 확인 필요'})
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <span style={{ fontSize: 12, color: '#065F46' }}>
                                                                잔여 {mt.remainingCount}회
                                                            </span>
                                                        </Row>
                                                    );
                                                })}
                                                <button onClick={() => setShowIssuePanel(true)}
                                                    style={{
                                                        marginTop: 4, height: 32, borderRadius: 6, fontSize: 12, fontWeight: 500,
                                                        background: 'white', border: '1px dashed #D1D5DB', color: '#6B7280',
                                                        cursor: 'pointer',
                                                    }}>
                                                    + 다른 수강권 추가로 발급
                                                </button>
                                            </Col>
                                        )}
                                    </>
                                )}
                            </Col>
                        )}

                        {preBook.length > 0 && ticketDeductionTotal > 0 && (
                            <span style={{
                                fontSize: 13, color: '#92400E', fontWeight: 600,
                                backgroundColor: '#FEF3C7', padding: '8px 12px', borderRadius: 6,
                                border: '1px solid #FDE68A',
                            }}>
                                ⚠ 총 {preBook.length}명 × {estimatedSessionCount}세션 = 수강권 {ticketDeductionTotal}회 차감
                            </span>
                        )}
                    </Col>
                )}

                <Row style={{ gap: 10, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid #F1F5F9' }}>
                    <button onClick={onClose}
                        style={{
                            height: 42, padding: '0 22px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                            backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', cursor: 'pointer',
                        }}>
                        취소
                    </button>
                    <button onClick={handleCreate}
                        disabled={!selectedTemplate || createMutation.isLoading}
                        style={{
                            height: 42, padding: '0 28px', borderRadius: 8, fontSize: 14, fontWeight: 700,
                            backgroundColor: '#3B82F6', color: 'white', border: 'none',
                            cursor: !selectedTemplate || createMutation.isLoading ? 'not-allowed' : 'pointer',
                            opacity: !selectedTemplate || createMutation.isLoading ? 0.5 : 1,
                        }}>
                        {createMutation.isLoading ? '생성 중...' : '생성하기'}
                    </button>
                </Row>
            </Col>
            {pauseGate.modal}
            {scheduleLock.gate}
        </Modal>
    );
};

const RemoveButton = ({ onClick, ariaLabel }: { onClick: () => void; ariaLabel?: string }) => {
    const [hover, setHover] = useState(false);
    return (
        <button
            type="button"
            aria-label={ariaLabel || '삭제'}
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                width: 26, height: 26, borderRadius: 6,
                background: hover ? '#FEE2E2' : 'transparent',
                border: 'none', cursor: 'pointer',
                color: hover ? '#DC2626' : '#9CA3AF',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background-color 0.15s, color 0.15s',
                padding: 0, flexShrink: 0,
            }}
        >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
        </button>
    );
};

const EligibleRow = ({ member, onAdd }: {
    member: {
        memberId: string;
        memberName: string;
        tickets: { mtId: string; name: string; remaining: number; isShared?: boolean; ownerName?: string }[];
    };
    onAdd: (mtId: string, name: string, remaining: number) => void;
}) => {
    const [expanded, setExpanded] = useState(false);
    const multiple = member.tickets.length > 1;
    // 공유받은 회차는 소유자를 밝혀야 잔여가 왜 같은지·누구 회차인지 알 수 있다.
    const sharedLabel = (t: { isShared?: boolean; ownerName?: string }) =>
        t.isShared ? ` · 공유(${t.ownerName || '소유자 확인 필요'})` : '';
    return (
        <Col style={{ borderRadius: 8, border: '1px solid #D1FAE5', backgroundColor: 'white', overflow: 'hidden' }}>
            <Row style={{ alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px' }}>
                <Col style={{ gap: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{member.memberName}</span>
                    <span style={{ fontSize: 11, color: '#6B7280' }}>
                        {multiple ? `수강권 ${member.tickets.length}개 보유`
                            : `${member.tickets[0].name} · 잔여 ${member.tickets[0].remaining}회${sharedLabel(member.tickets[0])}`}
                    </span>
                </Col>
                {multiple ? (
                    <button onClick={() => setExpanded(!expanded)}
                        style={{
                            fontSize: 12, fontWeight: 600, color: '#10B981',
                            background: 'none', border: '1px solid #A7F3D0',
                            borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                        }}>
                        {expanded ? '접기' : '수강권 선택'}
                    </button>
                ) : (
                    <button onClick={() => onAdd(member.tickets[0].mtId, member.tickets[0].name, member.tickets[0].remaining)}
                        style={{
                            fontSize: 12, fontWeight: 600, color: 'white',
                            background: '#10B981', border: 'none',
                            borderRadius: 6, padding: '4px 12px', cursor: 'pointer',
                        }}>
                        추가
                    </button>
                )}
            </Row>
            {multiple && expanded && (
                <Col style={{ borderTop: '1px solid #ECFDF5', padding: '6px 12px 10px', gap: 4, maxHeight: 200, overflowY: 'auto' }}>
                    {member.tickets.map((t) => (
                        <Row key={t.mtId} style={{
                            alignItems: 'center', justifyContent: 'space-between',
                            padding: '6px 10px', borderRadius: 6, backgroundColor: '#F0FDF4',
                            flexShrink: 0,
                        }}>
                            <span style={{ fontSize: 12, color: '#374151' }}>{t.name} · 잔여 {t.remaining}회{sharedLabel(t)}</span>
                            <button onClick={() => onAdd(t.mtId, t.name, t.remaining)}
                                style={{
                                    fontSize: 11, fontWeight: 600, color: 'white',
                                    background: '#10B981', border: 'none',
                                    borderRadius: 6, padding: '3px 10px', cursor: 'pointer',
                                    flexShrink: 0,
                                }}>
                                추가
                            </button>
                        </Row>
                    ))}
                </Col>
            )}
        </Col>
    );
};

export default QuickGroupSessionModal;
